import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';
import toast from 'react-hot-toast';
import { NavigateFunction } from 'react-router-dom';

import { allRoles, createRole, deleteRole, getAllowedPermissions, updateRole } from '@/api';

import { AllRole, createRoleFrom } from '@/interface';

export interface ProjectTeamMember {
  key: string;
  userId: string;
  name: string;
  email: string;
  status: string;
  role: string;
  roleId: string;
}

interface RoleErrorPayload {
  message: string;
  [key: string]: unknown;
}

const toRoleErrorPayload = (error: unknown, fallbackMessage: string): RoleErrorPayload => {
  const data = axios.isAxiosError(error) ? (error.response?.data as Partial<RoleErrorPayload> | undefined) : undefined;
  return { ...data, message: data?.message || fallbackMessage };
};

interface RoleState {
  loading: boolean;
  error: RoleErrorPayload | null;
  success: boolean;
  allRoles: AllRole[];
  selectedProjectTeamMember: ProjectTeamMember | null;
}
const initialState: RoleState = {
  loading: false,
  error: null,
  success: false,
  allRoles: [],
  selectedProjectTeamMember: null,
};

//create role
export const createRoleRequest = createAsyncThunk<
  AllRole,
  { data: createRoleFrom; navigate: (path: string) => void },
  { rejectValue: RoleErrorPayload }
>('roles/createRole', async ({ data, navigate }, { rejectWithValue }) => {
  try {
    const response = await createRole(data);
    toast.success('Role Created successfully');
    navigate('/user-roles?tab=roles');
    return response.data;
  } catch (error) {
    return rejectWithValue(toRoleErrorPayload(error, 'Created role failed'));
  }
});
// get all roles
export const allRolesRequest = createAsyncThunk<AllRole[], void, { rejectValue: RoleErrorPayload }>(
  'roles/allRoles',
  async (_, { rejectWithValue }) => {
    try {
      const response = await allRoles();
      return response.data;
    } catch (error) {
      return rejectWithValue(toRoleErrorPayload(error, 'Failed to fetch roles'));
    }
  },
);
export const updateRoleRequest = createAsyncThunk<
  AllRole,
  { id: string; data: createRoleFrom; navigate: NavigateFunction },
  { rejectValue: RoleErrorPayload }
>('updateRoleRequest', async ({ id, data, navigate }, { rejectWithValue }) => {
  try {
    const response = await updateRole(id, data);
    navigate(-1);
    return response.data;
  } catch (error) {
    return rejectWithValue(toRoleErrorPayload(error, 'Failed to create project'));
  }
});
export const getAllowedPermissionsListRequest = createAsyncThunk<
  { permissions: Record<string, 0 | 1> },
  void,
  { rejectValue: RoleErrorPayload }
>('roles/get-permissions-key', async (_, { rejectWithValue }) => {
  try {
    const response = await getAllowedPermissions();
    return response.data;
  } catch (error) {
    return rejectWithValue(toRoleErrorPayload(error, 'Failed to fetch roles'));
  }
});

export const deleteRoleRequest = createAsyncThunk<string, string, { rejectValue: RoleErrorPayload }>(
  'roles/deleteRole',
  async (id, { rejectWithValue }) => {
    try {
      await deleteRole(id);
      toast.success('Role deleted successfully');
      return id;
    } catch (error) {
      return rejectWithValue(toRoleErrorPayload(error, 'Failed to delete role'));
    }
  },
);

export const rolesSlice = createSlice({
  name: 'roles',
  initialState,
  reducers: {
    resetError: (state) => {
      state.error = null;
    },
    setSelectedProjectTeamMember: (state, action: PayloadAction<ProjectTeamMember>) => {
      state.selectedProjectTeamMember = action.payload;
    },
    clearSelectedProjectTeamMember: (state) => {
      state.selectedProjectTeamMember = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // handle all roles
      .addCase(allRolesRequest.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(allRolesRequest.fulfilled, (state, action) => {
        state.loading = false;
        state.allRoles = action.payload;
      })
      .addCase(allRolesRequest.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? null;
      })
      // get permissions list
      .addCase(getAllowedPermissionsListRequest.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAllowedPermissionsListRequest.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(getAllowedPermissionsListRequest.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? null;
      })
      //create role
      .addCase(createRoleRequest.pending, (state) => {
        state.loading = true;
        state.success = false;
        state.error = null;
      })
      .addCase(createRoleRequest.fulfilled, (state) => {
        state.loading = false;
        state.success = true;
      })
      .addCase(createRoleRequest.rejected, (state, action) => {
        state.loading = false;
        state.success = false;

        state.error = action.payload ?? null;
      })
      .addCase(deleteRoleRequest.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteRoleRequest.fulfilled, (state, action) => {
        state.loading = false;
        state.allRoles = state.allRoles.filter((role) => role.id !== action.payload);
      })
      .addCase(deleteRoleRequest.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? null;
      });
  },
});
export const { setSelectedProjectTeamMember, clearSelectedProjectTeamMember } = rolesSlice.actions;
